﻿
// LiveSessions.js
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  ScrollView,
  FlatList,
  Image,
  Dimensions,
  TextInput,
  Alert,
  Platform,
  Animated,
  Modal,
  RefreshControl,
  ActivityIndicator,
  Linking,
  Share,
  Clipboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { 
  MaterialCommunityIcons, 
  Ionicons, 
  Feather,
  AntDesign,
  FontAwesome5,
  Entypo,
} from '@expo/vector-icons';
import { useNavigation, useFocusEffect, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Video } from 'expo-video';
import { Audio } from 'expo-audio'; // if you need audio

import * as Calendar from 'expo-calendar';
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import Constants from 'expo-constants';
import { BlurView } from 'expo-blur';
import CountDown from 'react-native-countdown-component';

const { width, height } = Dimensions.get('window');

// Configure notifications for live sessions
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

const LiveSessions = () => {
  const navigation = useNavigation();
  const route = useRoute();

  // Coerce any value (Date, ISO string, timestamp) into a valid Date, or null.
  const toDate = (v) => {
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (v == null) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [liveSessions, setLiveSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming', 'live', 'past', 'my'
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    category: 'all',
    instructor: 'all',
    price: 'all',
  });
  const [countdowns, setCountdowns] = useState({});
  const [joinedSessions, setJoinedSessions] = useState([]);
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [notificationPermission, setNotificationPermission] = useState(false);
  
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;
  const scrollY = useRef(new Animated.Value(0)).current;
  const videoRef = useRef(null);
  const countdownRefs = useRef({});
  const searchTimeoutRef = useRef(null);

  // Animation for header
  const headerHeight = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [200, 120],
    extrapolate: 'clamp',
  });

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 80],
    outputRange: [1, 0.9],
    extrapolate: 'clamp',
  });

  useFocusEffect(
    React.useCallback(() => {
      loadInitialData();
      startAnimations();
      setupCountdownTimers();
      return () => {
        cleanupCountdownTimers();
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [])
  );

  useEffect(() => {
    filterSessions();
    setupCountdownTimers();
  }, [searchQuery, selectedFilters, liveSessions, activeTab]);

  useEffect(() => {
    // Request notification permissions
    requestNotificationPermissions();
    // Get user timezone
    setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load preferences
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
      
      // Load all data
      await Promise.all([
        loadLiveSessions(),
        loadJoinedSessions(),
        loadNotificationSettings(),
      ]);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      Alert.alert('Error', 'Failed to load live sessions');
    } finally {
      setLoading(false);
    }
  };

  const requestNotificationPermissions = async () => {
    try {
      if (Platform.OS === 'android') {
        await Notifications.setNotificationChannelAsync('live-sessions', {
          name: 'Live Session Reminders',
          importance: Notifications.AndroidImportance.MAX,
          vibrationPattern: [0, 250, 250, 250],
          lightColor: '#FF231F7C',
        });
      }

      const { status } = await Notifications.requestPermissionsAsync();
      setNotificationPermission(status === 'granted');
      
      if (status !== 'granted') {
        Alert.alert(
          'Notification Permission',
          'Enable notifications to get reminders for live sessions.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
    }
  };

  const loadLiveSessions = async () => {
    try {
      const savedSessions = await AsyncStorage.getItem('liveSessions');
      if (savedSessions) {
        const sessions = JSON.parse(savedSessions);
        setLiveSessions(sessions);
        setFilteredSessions(sessions);
      } else {
        const mockSessions = await generateMockSessions();
        setLiveSessions(mockSessions);
        setFilteredSessions(mockSessions);
        await AsyncStorage.setItem('liveSessions', JSON.stringify(mockSessions));
      }
    } catch (error) {
      console.error('Error loading live sessions:', error);
      const mockSessions = await generateMockSessions();
      setLiveSessions(mockSessions);
      setFilteredSessions(mockSessions);
    }
  };

  const generateMockSessions = async () => {
    const now = new Date();
    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    
    return [
      // Live Now
      {
        id: 'LS001',
        title: '🔴 LIVE NOW: Structural Engineering Workshop',
        subtitle: 'Real-time structural analysis and design discussion',
        description: 'Interactive workshop covering the latest structural engineering techniques, code requirements, and design challenges. Join us for live Q&A with industry experts.',
        longDescription: 'This live workshop focuses on practical structural engineering applications. We\'ll cover seismic design principles, load calculations, material selection, and modern construction techniques. Bring your questions for our panel of experts!',
        instructor: 'Dr. Robert Williams',
        instructorPhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
        instructorBio: 'PhD in Structural Engineering, 20+ years industry experience',
        category: 'civil',
        color: '#0e4e96',
        icon: 'bridge',
        status: 'live',
        startTime: new Date(now.getTime() - 30 * 60 * 1000), // Started 30 minutes ago
        endTime: new Date(now.getTime() + 90 * 60 * 1000), // Ends in 90 minutes
        duration: '2 hours',
        maxParticipants: 500,
        currentParticipants: 287,
        price: 0,
        originalPrice: 49,
        isFree: true,
        platform: 'Zoom',
        meetingId: '123 456 7890',
        meetingPassword: 'Engineering2024',
        meetingLink: 'https://zoom.us/j/1234567890?pwd=Engineering2024',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: true,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=400',
        tags: ['Structural', 'Workshop', 'Live Q&A', 'Civil'],
        joined: true,
        reminderSet: true,
        rating: 4.9,
        reviews: 128,
        certificateAvailable: true,
        requirements: ['Engineering background', 'Basic structural knowledge'],
        learningOutcomes: ['Advanced structural analysis', 'Code compliance', 'Practical design skills'],
        instructorRating: 4.8,
        sessionsConducted: 45,
      },
      // Starting Soon
      {
        id: 'LS002',
        title: '🚀 Python for Engineering Automation',
        subtitle: 'Live coding session on automation scripts',
        description: 'Learn how to automate engineering tasks using Python. Real-time coding, debugging, and optimization techniques.',
        longDescription: 'This live coding session will teach you how to create powerful automation scripts for engineering tasks. We\'ll cover data processing, CAD automation, report generation, and more. Follow along with the instructor in real-time.',
        instructor: 'Prof. Sarah Johnson',
        instructorPhoto: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=200',
        instructorBio: 'Computer Engineering Professor, Python Expert',
        category: 'computer',
        color: '#3776AB',
        icon: 'language-python',
        status: 'upcoming',
        startTime: new Date(now.getTime() + 45 * 60 * 1000), // Starts in 45 minutes
        endTime: new Date(now.getTime() + 165 * 60 * 1000),
        duration: '2 hours',
        maxParticipants: 300,
        currentParticipants: 156,
        price: 29,
        originalPrice: 49,
        isFree: false,
        platform: 'Microsoft Teams',
        meetingId: 'teams.live/engineering-python',
        meetingPassword: 'Python2024',
        meetingLink: 'https://teams.microsoft.com/l/meetup-join/19:meeting_xyz',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: false,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1526379879527-8559ecfcaec7?w=400',
        tags: ['Python', 'Automation', 'Coding', 'Engineering'],
        joined: false,
        reminderSet: true,
        rating: 4.8,
        reviews: 89,
        certificateAvailable: true,
        requirements: ['Basic Python knowledge', 'Engineering background'],
        learningOutcomes: ['Automation scripting', 'Engineering tool integration', 'Workflow optimization'],
        instructorRating: 4.9,
        sessionsConducted: 32,
      },
      // Today
      {
        id: 'LS003',
        title: '⚡ Electrical Systems Design Review',
        subtitle: 'Live design critique and optimization',
        description: 'Submit your electrical designs for live review and optimization by industry experts.',
        longDescription: 'Bring your electrical system designs for live critique and optimization. Our panel of electrical engineering experts will provide real-time feedback, suggest improvements, and share industry best practices.',
        instructor: 'Eng. Michael Chen',
        instructorPhoto: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200',
        instructorBio: 'Licensed Electrical Engineer, Design Expert',
        category: 'electrical',
        color: '#FFD700',
        icon: 'flash',
        status: 'upcoming',
        startTime: new Date(now.getTime() + 3 * oneHour), // Starts in 3 hours
        endTime: new Date(now.getTime() + 5 * oneHour),
        duration: '2 hours',
        maxParticipants: 150,
        currentParticipants: 89,
        price: 49,
        originalPrice: 79,
        isFree: false,
        platform: 'Google Meet',
        meetingId: 'meet.google.com/xyz-abc-def',
        meetingPassword: 'Electrical2024',
        meetingLink: 'https://meet.google.com/xyz-abc-def',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: true,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=400',
        tags: ['Electrical', 'Design', 'Review', 'Optimization'],
        joined: true,
        reminderSet: false,
        rating: 4.7,
        reviews: 56,
        certificateAvailable: true,
        requirements: ['Electrical engineering background', 'Design experience'],
        learningOutcomes: ['Design optimization', 'Code compliance', 'Industry standards'],
        instructorRating: 4.7,
        sessionsConducted: 28,
      },
      // Tomorrow
      {
        id: 'LS004',
        title: '🏗️ Advanced CAD & BIM Workshop',
        subtitle: 'Live 3D modeling and BIM implementation',
        description: 'Master advanced CAD techniques and BIM implementation in this interactive workshop.',
        longDescription: 'This comprehensive workshop covers advanced CAD modeling, BIM implementation, clash detection, and collaboration workflows. Learn from industry professionals with hands-on exercises.',
        instructor: 'Eng. David Miller',
        instructorPhoto: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200',
        instructorBio: 'CAD/BIM Specialist, Certified Instructor',
        category: 'mechanical',
        color: '#FF6B35',
        icon: 'cog',
        status: 'upcoming',
        startTime: new Date(now.getTime() + oneDay), // Tomorrow
        endTime: new Date(now.getTime() + oneDay + 3 * oneHour),
        duration: '3 hours',
        maxParticipants: 200,
        currentParticipants: 134,
        price: 79,
        originalPrice: 129,
        isFree: false,
        platform: 'Zoom',
        meetingId: '456 789 0123',
        meetingPassword: 'CAD2024',
        meetingLink: 'https://zoom.us/j/4567890123?pwd=CAD2024',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: true,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
        tags: ['CAD', 'BIM', '3D Modeling', 'Workshop'],
        joined: false,
        reminderSet: false,
        rating: 4.9,
        reviews: 112,
        certificateAvailable: true,
        requirements: ['Basic CAD knowledge', 'Engineering background'],
        learningOutcomes: ['Advanced modeling', 'BIM workflows', 'Collaboration techniques'],
        instructorRating: 4.8,
        sessionsConducted: 41,
      },
      // This Week
      {
        id: 'LS005',
        title: '🧪 Chemical Process Optimization',
        subtitle: 'Live process simulation and optimization',
        description: 'Optimize chemical processes with real-time simulation and expert guidance.',
        longDescription: 'Learn advanced techniques for chemical process optimization using simulation software. This session includes live demonstrations, case studies, and interactive problem-solving.',
        instructor: 'Dr. Emily Watson',
        instructorPhoto: 'https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?w=200',
        instructorBio: 'Chemical Engineering Professor, Process Optimization Expert',
        category: 'chemical',
        color: '#4CAF50',
        icon: 'flask',
        status: 'upcoming',
        startTime: new Date(now.getTime() + 2 * oneDay), // In 2 days
        endTime: new Date(now.getTime() + 2 * oneDay + 2.5 * oneHour),
        duration: '2.5 hours',
        maxParticipants: 180,
        currentParticipants: 76,
        price: 59,
        originalPrice: 99,
        isFree: false,
        platform: 'Microsoft Teams',
        meetingId: 'teams.live/chemical-process',
        meetingPassword: 'Chemical2024',
        meetingLink: 'https://teams.microsoft.com/l/meetup-join/19:meeting_xyz',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: false,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400',
        tags: ['Chemical', 'Optimization', 'Simulation', 'Process'],
        joined: false,
        reminderSet: false,
        rating: 4.6,
        reviews: 67,
        certificateAvailable: true,
        requirements: ['Chemical engineering background', 'Basic process knowledge'],
        learningOutcomes: ['Process optimization', 'Simulation skills', 'Efficiency improvement'],
        instructorRating: 4.7,
        sessionsConducted: 23,
      },
      // Past Session
      {
        id: 'LS006',
        title: '🤖 Machine Learning in Engineering',
        subtitle: 'AI applications for engineering problems',
        description: 'Explore how machine learning can solve complex engineering challenges.',
        longDescription: 'This past session covered machine learning applications in various engineering domains. Participants learned about predictive maintenance, quality control, and optimization algorithms.',
        instructor: 'Dr. Alex Rodriguez',
        instructorPhoto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200',
        instructorBio: 'AI Researcher, Engineering Applications Specialist',
        category: 'computer',
        color: '#FF4081',
        icon: 'robot',
        status: 'past',
        startTime: new Date(now.getTime() - 2 * oneDay), // 2 days ago
        endTime: new Date(now.getTime() - 2 * oneDay + 2 * oneHour),
        duration: '2 hours',
        maxParticipants: 400,
        currentParticipants: 321,
        price: 49,
        originalPrice: 79,
        isFree: false,
        platform: 'Zoom',
        meetingId: '789 012 3456',
        meetingPassword: 'ML2024',
        meetingLink: 'https://zoom.us/recording/123456',
        recordingAvailable: true,
        recordingLink: 'https://zoom.us/recording/123456',
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: true,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400',
        tags: ['Machine Learning', 'AI', 'Engineering', 'Past Session'],
        joined: true,
        reminderSet: false,
        rating: 4.8,
        reviews: 94,
        certificateAvailable: true,
        requirements: ['Basic programming', 'Engineering background'],
        learningOutcomes: ['ML applications', 'Model implementation', 'Engineering problem-solving'],
        instructorRating: 4.8,
        sessionsConducted: 36,
      },
      // Free Session
      {
        id: 'LS007',
        title: '📊 Engineering Mathematics Review',
        subtitle: 'Free live review session for engineering math',
        description: 'Brush up on essential engineering mathematics concepts in this free session.',
        longDescription: 'This free session covers calculus, linear algebra, and differential equations essential for engineering students. Perfect for exam preparation or concept review.',
        instructor: 'Dr. James Wilson',
        instructorPhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200',
        instructorBio: 'Mathematics Professor, Engineering Applications',
        category: 'mathematics',
        color: '#9B30FF',
        icon: 'calculator',
        status: 'upcoming',
        startTime: new Date(now.getTime() + 3 * oneDay), // In 3 days
        endTime: new Date(now.getTime() + 3 * oneDay + 3 * oneHour),
        duration: '3 hours',
        maxParticipants: 1000,
        currentParticipants: 456,
        price: 0,
        originalPrice: 0,
        isFree: true,
        platform: 'YouTube Live',
        meetingId: 'YouTube Live Stream',
        meetingPassword: '',
        meetingLink: 'https://youtube.com/live/engineering-math',
        recordingAvailable: true,
        materialsAvailable: true,
        qnaEnabled: true,
        pollEnabled: true,
        breakoutRooms: false,
        whiteboardEnabled: true,
        chatEnabled: true,
        thumbnail: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400',
        tags: ['Mathematics', 'Free', 'Review', 'Engineering Math'],
        joined: false,
        reminderSet: true,
        rating: 4.7,
        reviews: 203,
        certificateAvailable: false,
        requirements: ['High school mathematics', 'Engineering student'],
        learningOutcomes: ['Calculus review', 'Linear algebra', 'Differential equations'],
        instructorRating: 4.9,
        sessionsConducted: 58,
      },
    ];
  };

  const loadJoinedSessions = async () => {
    try {
      const joined = await AsyncStorage.getItem('joinedLiveSessions') || '[]';
      setJoinedSessions(JSON.parse(joined));
    } catch (error) {
      console.error('Error loading joined sessions:', error);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const settings = await AsyncStorage.getItem('notificationSettings');
      if (settings) {
        // Parse and apply settings if needed
      }
    } catch (error) {
      console.error('Error loading notification settings:', error);
    }
  };

  const setupCountdownTimers = () => {
    const newCountdowns = {};
    liveSessions.forEach(session => {
      if (session.status === 'upcoming' || session.status === 'live') {
        const start = toDate(session.startTime);
        if (!start) return;
        const timeRemaining = Math.max(0, start.getTime() - new Date().getTime());
        if (timeRemaining > 0) {
          newCountdowns[session.id] = timeRemaining;
        }
      }
    });
    setCountdowns(newCountdowns);
  };

  const cleanupCountdownTimers = () => {
    // Clean up any timers if needed
  };

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadInitialData();
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const filterSessions = () => {
    let filtered = [...liveSessions];
    
    // Apply status filter based on active tab
    switch (activeTab) {
      case 'live':
        filtered = filtered.filter(session => session.status === 'live');
        break;
      case 'upcoming':
        filtered = filtered.filter(session => session.status === 'upcoming');
        break;
      case 'past':
        filtered = filtered.filter(session => session.status === 'past');
        break;
      case 'my':
        filtered = filtered.filter(session => session.joined);
        break;
      default:
        break;
    }
    
    // Apply category filter
    if (selectedFilters.category !== 'all') {
      filtered = filtered.filter(session => session.category === selectedFilters.category);
    }
    
    // Apply price filter
    if (selectedFilters.price !== 'all') {
      if (selectedFilters.price === 'free') {
        filtered = filtered.filter(session => session.isFree);
      } else if (selectedFilters.price === 'paid') {
        filtered = filtered.filter(session => !session.isFree);
      }
    }
    
    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(session =>
        String(session.title ?? '').toLowerCase().includes(query) ||
        String(session.subtitle ?? '').toLowerCase().includes(query) ||
        String(session.instructor ?? '').toLowerCase().includes(query) ||
        session.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Sort by start time
    filtered.sort((a, b) => a.startTime - b.startTime);
    
    setFilteredSessions(filtered);
  };

  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      filterSessions();
    }, 300);
  };

  const joinSession = async (session) => {
    try {
      // Check if session is live or upcoming
      if (session.status === 'past') {
        Alert.alert(
          'Session Ended',
          'This session has ended. You can watch the recording if available.',
          [{ text: 'OK' }]
        );
        return;
      }

      // Check if already joined
      if (session.joined) {
        setSelectedSession(session);
        setShowJoinModal(true);
        return;
      }

      // For paid sessions, go through the payment screen first.
      if (!session.isFree) {
        const priceNum = Number(session.price) || 0;
        const scenario = {
          id:            `livesession_${session.id}_${Date.now()}`,
          type:          'live_session',
          category:      'learning',
          name:          session.title,
          description:   `Live session: ${session.title}${session.instructor ? ` with ${session.instructor}` : ''}`,
          amountInPence: Math.round(priceNum * 100),
          currency:      'GBP',
          metadata: {
            session_id:    session.id,
            session_title: session.title,
            instructor:    session.instructor || '',
          },
          successRoute:   'LiveSessions',
          successParams:  { joinedSessionId: session.id },
          successMessage: `You're registered for "${session.title}".`,
        };
        navigation.navigate('PaymentScreen', { scenario });
      } else {
        await completeJoin(session);
      }
    } catch (error) {
      console.error('Error joining session:', error);
      Alert.alert('Error', 'Failed to join session');
    }
  };

  const completeJoin = async (session) => {
    try {
      // Update session as joined
      const updatedSessions = liveSessions.map(s => 
        s.id === session.id ? { ...s, joined: true } : s
      );
      setLiveSessions(updatedSessions);
      await AsyncStorage.setItem('liveSessions', JSON.stringify(updatedSessions));
      
      // Add to joined sessions
      const updatedJoined = [...joinedSessions, session.id];
      setJoinedSessions(updatedJoined);
      await AsyncStorage.setItem('joinedLiveSessions', JSON.stringify(updatedJoined));
      
      // Show join modal
      setSelectedSession({...session, joined: true});
      setShowJoinModal(true);
      
    } catch (error) {
      console.error('Error completing join:', error);
      throw error;
    }
  };

  // After a successful payment, PaymentScreen returns here with the session id.
  useEffect(() => {
    const id = route?.params?.joinedSessionId;
    if (id == null) return;
    const session = liveSessions.find((s) => s.id === id);
    if (session) {
      completeJoin(session).catch((e) => console.error('Post-payment join failed:', e));
    }
    navigation.setParams({ joinedSessionId: undefined });
  }, [route?.params?.joinedSessionId, liveSessions]);

  const openSessionLink = async (session) => {
    try {
      await Linking.openURL(session.meetingLink);
    } catch (error) {
      console.error('Error opening session link:', error);
      try {
        await Clipboard.setStringAsync(`Meeting ID: ${session.meetingId}\nPassword: ${session.meetingPassword}\nLink: ${session.meetingLink}`);
        Alert.alert(
          'Meeting Details Copied',
          'Meeting details have been copied to clipboard. Please paste them in your meeting app.',
          [{ text: 'OK' }]
        );
      } catch (e) {
        Alert.alert('Error', 'Failed to open meeting link');
      }
    }
  };

  const scheduleReminder = async (session) => {
    try {
      if (!notificationPermission) {
        Alert.alert(
          'Notifications Disabled',
          'Please enable notifications in settings to set reminders.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Open Settings', onPress: () => Linking.openSettings() },
          ]
        );
        return;
      }

      // Schedule notification 15 minutes before session
      const _start = toDate(session.startTime);
      if (!_start) return;
      const trigger = new Date(_start.getTime() - 15 * 60 * 1000);
      
      await Notifications.scheduleNotificationAsync({
        content: {
          title: `⏰ Live Session Reminder`,
          body: `${session.title} starts in 15 minutes!`,
          data: { sessionId: session.id, type: 'live-session-reminder' },
          sound: 'default',
        },
        trigger,
      });

      // Update session reminder status
      const updatedSessions = liveSessions.map(s => 
        s.id === session.id ? { ...s, reminderSet: true } : s
      );
      setLiveSessions(updatedSessions);
      await AsyncStorage.setItem('liveSessions', JSON.stringify(updatedSessions));

      Alert.alert(
        'Reminder Set',
        `You'll be notified 15 minutes before the session starts.`,
        [{ text: 'OK' }]
      );
      
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      Alert.alert('Error', 'Failed to set reminder');
    }
  };

  const addToCalendar = async (session) => {
    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();
      if (status === 'granted') {
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);
        const defaultCalendar = calendars.find(c => c.isPrimary) || calendars[0];
        
        if (defaultCalendar) {
          await Calendar.createEventAsync(defaultCalendar.id, {
            title: `📅 Live Session: ${session.title}`,
            startDate: session.startTime,
            endDate: session.endTime,
            timeZone: userTimezone,
            location: session.platform,
            notes: `${session.description}\n\nMeeting ID: ${session.meetingId}\nPassword: ${session.meetingPassword}\nLink: ${session.meetingLink}`,
            url: session.meetingLink,
          });

          Alert.alert(
            'Added to Calendar',
            'Session has been added to your calendar.',
            [{ text: 'OK' }]
          );
        }
      }
    } catch (error) {
      console.error('Error adding to calendar:', error);
      Alert.alert('Error', 'Failed to add to calendar');
    }
  };

  const shareSession = async (session) => {
    try {
      await Share.share({
        title: `🎥 Live Session: ${session.title}`,
        message: `Join me for this live engineering session:\n\n${session.title}\n${session.subtitle}\n\nInstructor: ${session.instructor}\nDate: ${formatDate(session.startTime)}\nTime: ${formatTime(session.startTime)}\n\nMeeting Link: ${session.meetingLink}`,
        url: session.meetingLink,
      });
    } catch (error) {
      console.error('Error sharing session:', error);
    }
  };

  const formatDate = (date) => {
    const d = toDate(date);
    if (!d) return 'Date TBA';
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (date) => {
    const d = toDate(date);
    if (!d) return 'Time TBA';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const formatCountdown = (session) => {
    if (session.status === 'live') {
      return 'LIVE NOW';
    }

    const start = toDate(session.startTime);
    if (!start) return 'Scheduled';
    const timeRemaining = start.getTime() - new Date().getTime();
    if (timeRemaining <= 0) {
      return 'Starting soon...';
    }

    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `Starts in ${hours}h ${minutes}m`;
    } else {
      return `Starts in ${minutes}m`;
    }
  };

  const getSessionStatus = (session) => {
    const now = new Date();
    const start = toDate(session.startTime);
    const end = toDate(session.endTime);
    if (start && end && start <= now && end >= now) {
      return 'live';
    } else if (start && start > now) {
      return 'upcoming';
    } else {
      return 'past';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return '#ef4444';
      case 'upcoming': return '#f59e0b';
      case 'past': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'live': return 'record-circle';
      case 'upcoming': return 'clock-alert';
      case 'past': return 'history';
      default: return 'clock';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'computer': return 'laptop';
      case 'civil': return 'bridge';
      case 'mechanical': return 'cog';
      case 'electrical': return 'flash';
      case 'chemical': return 'flask';
      case 'mathematics': return 'calculator';
      default: return 'video';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'computer': return '#3776AB';
      case 'civil': return '#0e4e96';
      case 'mechanical': return '#FF6B35';
      case 'electrical': return '#FFD700';
      case 'chemical': return '#4CAF50';
      case 'mathematics': return '#9B30FF';
      default: return '#667eea';
    }
  };

  const categories = [
    { id: 'all', label: 'All Categories', icon: 'grid' },
    { id: 'computer', label: 'Computer', icon: 'laptop' },
    { id: 'civil', label: 'Civil', icon: 'bridge' },
    { id: 'mechanical', label: 'Mechanical', icon: 'cog' },
    { id: 'electrical', label: 'Electrical', icon: 'flash' },
    { id: 'chemical', label: 'Chemical', icon: 'flask' },
    { id: 'mathematics', label: 'Mathematics', icon: 'calculator' },
  ];

  const tabs = [
    { id: 'upcoming', label: 'Upcoming', icon: 'calendar-clock', count: liveSessions.filter(s => s.status === 'upcoming').length },
    { id: 'live', label: 'Live Now', icon: 'record-circle', count: liveSessions.filter(s => s.status === 'live').length },
    { id: 'past', label: 'Past', icon: 'history', count: liveSessions.filter(s => s.status === 'past').length },
    { id: 'my', label: 'My Sessions', icon: 'bookmark', count: joinedSessions.length },
  ];

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={[styles.loadingText, { color: darkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading Live Sessions...
      </Text>
    </View>
  );

  const renderHeader = () => (
    <Animated.View 
      style={[
        styles.header,
        {
          height: headerHeight,
          opacity: headerOpacity,
        },
      ]}
    >
      <LinearGradient
        colors={darkMode ? ['#2d3748', '#4a5568'] : ['#ef4444', '#f59e0b']}
        style={styles.headerGradient}
      >
        <View style={styles.headerTop}>
          <TouchableOpacity 
            style={styles.menuButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>🎥 Live Sessions</Text>
          
          <TouchableOpacity 
            style={styles.iconButton}
            onPress={() => navigation.navigate('ScheduleSession')}
          >
            <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
          </TouchableOpacity>
        </View>
        
        <Text style={styles.headerSubtitle}>
          Join interactive engineering sessions in real-time
        </Text>
        
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {liveSessions.filter(s => s.status === 'live').length}
            </Text>
            <Text style={styles.statLabel}>Live Now</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {liveSessions.filter(s => s.status === 'upcoming').length}
            </Text>
            <Text style={styles.statLabel}>Upcoming</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {joinedSessions.length}
            </Text>
            <Text style={styles.statLabel}>Joined</Text>
          </View>
          
          <View style={styles.statItem}>
            <Text style={styles.statNumber}>
              {liveSessions.filter(s => s.isFree).length}
            </Text>
            <Text style={styles.statLabel}>Free</Text>
          </View>
        </View>
      </LinearGradient>
    </Animated.View>
  );

  const renderSearchBar = () => (
    <View style={[styles.searchContainer, { 
      backgroundColor: darkMode ? '#2d3748' : '#ffffff',
      shadowColor: darkMode ? '#000' : '#ef4444',
    }]}>
      <View style={styles.searchInputContainer}>
        <MaterialCommunityIcons 
          name="magnify" 
          size={20} 
          color={darkMode ? '#a0aec0' : '#6b7280'} 
        />
        <TextInput
          style={[styles.searchInput, { color: darkMode ? '#f7fafc' : '#1f2937' }]}
          placeholder="Search live sessions, instructors..."
          placeholderTextColor={darkMode ? '#a0aec0' : '#6b7280'}
          value={searchQuery}
          onChangeText={handleSearch}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons 
              name="close-circle" 
              size={18} 
              color={darkMode ? '#a0aec0' : '#6b7280'} 
            />
          </TouchableOpacity>
        )}
      </View>
      
      <TouchableOpacity
        style={[styles.filterButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
        onPress={() => setShowFilters(true)}
      >
        <MaterialCommunityIcons 
          name="filter-variant" 
          size={18} 
          color={darkMode ? '#ffffff' : '#1f2937'} 
        />
        {Object.values(selectedFilters).some(val => val !== 'all') && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>!</Text>
          </View>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tabButton,
              { backgroundColor: darkMode ? '#2d3748' : '#ffffff' },
              activeTab === tab.id && { backgroundColor: getStatusColor(activeTab) }
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={18} 
              color={activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
            />
            <Text style={[
              styles.tabText,
              { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
            ]}>
              {tab.label}
            </Text>
            {tab.count > 0 && (
              <View style={[
                styles.tabCount,
                { backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : (darkMode ? '#4a5568' : '#e5e7eb') }
              ]}>
                <Text style={[
                  styles.tabCountText,
                  { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280') }
                ]}>
                  {tab.count}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderLiveNowBanner = () => {
    const liveSessionsCount = liveSessions.filter(s => s.status === 'live').length;
    if (liveSessionsCount === 0) return null;
    
    return (
      <TouchableOpacity
        style={styles.liveBanner}
        onPress={() => setActiveTab('live')}
      >
        <LinearGradient
          colors={['#ef4444', '#f59e0b']}
          style={styles.liveBannerGradient}
        >
          <View style={styles.liveIndicator}>
            <View style={styles.livePulse} />
            <Text style={styles.liveText}>LIVE</Text>
          </View>
          <Text style={styles.liveBannerText}>
            {liveSessionsCount} session{liveSessionsCount !== 1 ? 's' : ''} happening now!
          </Text>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />
        </LinearGradient>
      </TouchableOpacity>
    );
  };

  const renderSessionCard = (session) => (
    <Animated.View 
      style={[
        styles.sessionCard,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: slideAnim,
          }],
        },
      ]}
      key={session.id}
    >
      <TouchableOpacity
        style={[styles.sessionCardContent, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}
        onPress={() => {
          setSelectedSession(session);
          setShowSessionDetails(true);
        }}
        activeOpacity={0.9}
      >
        {/* Session Status Badge */}
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(session.status) }]}>
          <MaterialCommunityIcons 
            name={getStatusIcon(session.status)} 
            size={12} 
            color="#ffffff" 
          />
          <Text style={styles.statusText}>{String(session.status ?? '').toUpperCase()}</Text>
        </View>
        
        {/* Session Image */}
        <View style={styles.sessionImageContainer}>
          <Image 
            source={{ uri: (session.thumbnail && typeof session.thumbnail=="string" && session.thumbnail.startsWith("http")) ? session.thumbnail : undefined }} 
            style={styles.sessionImage}
            resizeMode="cover"
          />
          
          {/* Overlay Gradient */}
          <LinearGradient
            colors={['transparent', 'rgba(0,0,0,0.7)']}
            style={styles.imageOverlay}
          />
          
          {/* Session Info Overlay */}
          <View style={styles.sessionImageInfo}>
            <LinearGradient
              colors={[session.color, `${session.color}80`]}
              style={styles.categoryBadge}
            >
              <MaterialCommunityIcons name={session.icon} size={16} color="#ffffff" />
              <Text style={styles.categoryText}>
                {session.category.charAt(0).toUpperCase() + session.category.slice(1)}
              </Text>
            </LinearGradient>
            
            <View style={styles.sessionTimeInfo}>
              <MaterialCommunityIcons name="calendar" size={14} color="#ffffff" />
              <Text style={styles.sessionDateText}>
                {formatDate(session.startTime)}
              </Text>
            </View>
            
            <View style={styles.sessionTimeInfo}>
              <MaterialCommunityIcons name="clock" size={14} color="#ffffff" />
              <Text style={styles.sessionTimeText}>
                {formatTime(session.startTime)} - {formatTime(session.endTime)}
              </Text>
            </View>
          </View>
        </View>
        
        <View style={styles.sessionContent}>
          {/* Session Header */}
          <View style={styles.sessionHeader}>
            <View style={styles.sessionTitleContainer}>
              <Text style={[styles.sessionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]} numberOfLines={2}>
                {session.title}
              </Text>
              <Text style={[styles.sessionSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]} numberOfLines={1}>
                {session.subtitle}
              </Text>
            </View>
            
            {session.joined && (
              <View style={styles.joinedBadge}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#10b981" />
              </View>
            )}
          </View>
          
          {/* Instructor Info */}
          <View style={styles.instructorInfo}>
            <Image 
              source={{ uri: (session.instructorPhoto && typeof session.instructorPhoto=="string" && session.instructorPhoto.startsWith("http")) ? session.instructorPhoto : undefined }} 
              style={styles.instructorImage}
            />
            <View style={styles.instructorDetails}>
              <Text style={[styles.instructorName, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                {session.instructor}
              </Text>
              <View style={styles.instructorRating}>
                <MaterialCommunityIcons name="star" size={12} color="#fbbf24" />
                <Text style={[styles.ratingText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {session.instructorRating.toFixed(1)}
                </Text>
                <Text style={[styles.sessionsText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  • {session.sessionsConducted} sessions
                </Text>
              </View>
            </View>
          </View>
          
          {/* Session Details */}
          <View style={styles.sessionDetails}>
            <View style={styles.detailRow}>
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="account-group" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {session.currentParticipants}/{session.maxParticipants}
                </Text>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="video" size={14} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {session.platform}
                </Text>
              </View>
              
              <View style={styles.detailItem}>
                <MaterialCommunityIcons name="certificate" size={14} color={session.certificateAvailable ? "#10b981" : (darkMode ? '#a0aec0' : '#6b7280')} />
                <Text style={[styles.detailText, { 
                  color: session.certificateAvailable ? "#10b981" : (darkMode ? '#a0aec0' : '#6b7280')
                }]}>
                  {session.certificateAvailable ? 'Certificate' : 'No Cert'}
                </Text>
              </View>
            </View>
            
            {/* Countdown Timer */}
            {(session.status === 'upcoming' || session.status === 'live') && (
              <View style={styles.countdownContainer}>
                <MaterialCommunityIcons name="clock-alert" size={16} color={getStatusColor(session.status)} />
                <Text style={[styles.countdownText, { color: getStatusColor(session.status) }]}>
                  {formatCountdown(session)}
                </Text>
              </View>
            )}
          </View>
          
          {/* Tags */}
          {session.tags && session.tags.length > 0 && (
            <View style={styles.sessionTags}>
              {session.tags.slice(0, 3).map((tag, index) => (
                <View 
                  key={index} 
                  style={[styles.tag, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                >
                  <Text style={[styles.tagText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                    {tag}
                  </Text>
                </View>
              ))}
            </View>
          )}
          
          {/* Action Buttons */}
          <View style={styles.sessionActions}>
            <View style={styles.priceContainer}>
              {session.isFree ? (
                <Text style={[styles.freePrice, { color: '#10b981' }]}>FREE</Text>
              ) : (
                <>
                  <Text style={[styles.currentPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    ${session.price}
                  </Text>
                  {session.originalPrice > session.price && (
                    <Text style={[styles.originalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      ${session.originalPrice}
                    </Text>
                  )}
                </>
              )}
            </View>
            
            <View style={styles.actionButtons}>
              {session.status === 'past' ? (
                <TouchableOpacity
                  style={[styles.actionButton, styles.recordingButton]}
                  onPress={(e) => {
                    e.stopPropagation();
                    if (session.recordingLink) {
                      Linking.openURL(session.recordingLink);
                    } else {
                      Alert.alert('Recording', 'Recording will be available soon.');
                    }
                  }}
                >
                  <MaterialCommunityIcons name="play-circle" size={16} color="#ffffff" />
                  <Text style={styles.recordingButtonText}>Watch Recording</Text>
                </TouchableOpacity>
              ) : session.status === 'live' ? (
                <TouchableOpacity
                  style={[styles.actionButton, styles.joinButton]}
                  onPress={(e) => {
                    e.stopPropagation();
                    joinSession(session);
                  }}
                >
                  <MaterialCommunityIcons name="video" size={16} color="#ffffff" />
                  <Text style={styles.joinButtonText}>Join Now</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={[
                    styles.actionButton, 
                    session.joined ? styles.viewButton : styles.joinButton
                  ]}
                  onPress={(e) => {
                    e.stopPropagation();
                    joinSession(session);
                  }}
                >
                  <MaterialCommunityIcons 
                    name={session.joined ? "eye" : "calendar-plus"} 
                    size={16} 
                    color="#ffffff" 
                  />
                  <Text style={styles.joinButtonText}>
                    {session.joined ? 'View Details' : 'Join Session'}
                  </Text>
                </TouchableOpacity>
              )}
              
              <TouchableOpacity
                style={[styles.iconButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                onPress={(e) => {
                  e.stopPropagation();
                  if (session.status === 'upcoming' && !session.reminderSet) {
                    scheduleReminder(session);
                  } else if (session.status === 'upcoming') {
                    addToCalendar(session);
                  } else {
                    shareSession(session);
                  }
                }}
              >
                <MaterialCommunityIcons 
                  name={
                    session.status === 'upcoming' && !session.reminderSet ? "bell-outline" :
                    session.status === 'upcoming' ? "calendar-plus" : "share-variant"
                  } 
                  size={16} 
                  color={darkMode ? '#ffffff' : '#1f2937'} 
                />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons 
        name={activeTab === 'live' ? "video-off" : "calendar-blank"} 
        size={64} 
        color={darkMode ? '#4a5568' : '#d1d5db'} 
      />
      <Text style={[styles.emptyStateTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
        {activeTab === 'live' ? 'No Live Sessions' :
         activeTab === 'upcoming' ? 'No Upcoming Sessions' :
         activeTab === 'past' ? 'No Past Sessions' : 'No Joined Sessions'}
      </Text>
      <Text style={[styles.emptyStateText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {searchQuery 
          ? 'Try adjusting your search or filters'
          : activeTab === 'my' 
            ? 'Join sessions to see them here'
            : 'Check back later for new sessions'}
      </Text>
      
      {activeTab !== 'my' && (
        <TouchableOpacity
          style={styles.emptyStateButton}
          onPress={() => navigation.navigate('ScheduleSession')}
        >
          <MaterialCommunityIcons name="calendar-plus" size={20} color="#ffffff" />
          <Text style={styles.emptyStateButtonText}>Schedule a Session</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      transparent={true}
      onRequestClose={() => setShowFilters(false)}
    >
      <TouchableOpacity 
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowFilters(false)}
      >
        <View style={[styles.filtersModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.filtersHeader}>
            <Text style={[styles.filtersTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              🔍 Filter Sessions
            </Text>
            <TouchableOpacity onPress={() => setShowFilters(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.filtersContent} showsVerticalScrollIndicator={false}>
            {/* Category Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Category
              </Text>
              <View style={styles.filterOptions}>
                {categories.map((category) => (
                  <TouchableOpacity
                    key={category.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.category === category.id && { 
                        backgroundColor: getCategoryColor(category.id),
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, category: category.id})}
                  >
                    <MaterialCommunityIcons 
                      name={category.icon} 
                      size={16} 
                      color={selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.category === category.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {category.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Price Filter */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Price
              </Text>
              <View style={styles.filterOptions}>
                {[
                  { id: 'all', label: 'All Prices', icon: 'cash' },
                  { id: 'free', label: 'Free Only', icon: 'currency-usd-off' },
                  { id: 'paid', label: 'Paid Only', icon: 'credit-card' },
                ].map((price) => (
                  <TouchableOpacity
                    key={price.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      selectedFilters.price === price.id && { 
                        backgroundColor: price.id === 'free' ? '#10b981' : '#667eea',
                      }
                    ]}
                    onPress={() => setSelectedFilters({...selectedFilters, price: price.id})}
                  >
                    <MaterialCommunityIcons 
                      name={price.icon} 
                      size={16} 
                      color={selectedFilters.price === price.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: selectedFilters.price === price.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {price.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            
            {/* Status Filter (Auto based on active tab) */}
            <View style={styles.filterSection}>
              <Text style={[styles.filterSectionTitle, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Current View
              </Text>
              <View style={styles.filterOptions}>
                {tabs.map((tab) => (
                  <TouchableOpacity
                    key={tab.id}
                    style={[
                      styles.filterOption,
                      { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' },
                      activeTab === tab.id && { 
                        backgroundColor: getStatusColor(tab.id),
                      }
                    ]}
                    onPress={() => {
                      setActiveTab(tab.id);
                      setShowFilters(false);
                    }}
                  >
                    <MaterialCommunityIcons 
                      name={tab.icon} 
                      size={16} 
                      color={activeTab === tab.id ? '#ffffff' : (darkMode ? '#a0aec0' : '#6b7280')} 
                    />
                    <Text style={[
                      styles.filterOptionText,
                      { color: activeTab === tab.id ? '#ffffff' : (darkMode ? '#cbd5e0' : '#4b5563') }
                    ]}>
                      {tab.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>
          
          <View style={[styles.filtersActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity
              style={[styles.filterActionButton, styles.resetButton]}
              onPress={() => {
                setSelectedFilters({ category: 'all', instructor: 'all', price: 'all' });
                setActiveTab('upcoming');
                setSearchQuery('');
              }}
            >
              <MaterialCommunityIcons name="filter-remove" size={20} color="#ef4444" />
              <Text style={styles.resetButtonText}>Reset All</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.filterActionButton, styles.applyButton]}
              onPress={() => {
                setShowFilters(false);
                filterSessions();
              }}
            >
              <MaterialCommunityIcons name="check" size={20} color="#ffffff" />
              <Text style={styles.applyButtonText}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderSessionDetailsModal = () => (
    <Modal
      visible={showSessionDetails}
      animationType="slide"
      onRequestClose={() => setShowSessionDetails(false)}
    >
      {selectedSession && (
        <SafeAreaView style={[styles.modalContainer, { backgroundColor: darkMode ? '#1a202c' : '#ffffff' }]}>
          <View style={[styles.modalHeader, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity onPress={() => setShowSessionDetails(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
            
            <Text style={[styles.modalTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={1}>
              Session Details
            </Text>
            
            <TouchableOpacity onPress={() => shareSession(selectedSession)}>
              <MaterialCommunityIcons name="share-variant" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Session Hero */}
            <View style={styles.sessionHero}>
              <Image 
                source={{ uri: (selectedSession.thumbnail && typeof selectedSession.thumbnail=="string" && selectedSession.thumbnail.startsWith("http")) ? selectedSession.thumbnail : undefined }} 
                style={styles.sessionHeroImage}
                resizeMode="cover"
              />
              
              <View style={styles.sessionHeroOverlay}>
                <View style={[styles.heroStatusBadge, { backgroundColor: getStatusColor(selectedSession.status) }]}>
                  <MaterialCommunityIcons 
                    name={getStatusIcon(selectedSession.status)} 
                    size={16} 
                    color="#ffffff" 
                  />
                  <Text style={styles.heroStatusText}>{String(selectedSession.status ?? '').toUpperCase()}</Text>
                </View>
                
                <LinearGradient
                  colors={['transparent', darkMode ? '#1a202c' : '#ffffff']}
                  style={styles.sessionHeroGradient}
                />
                
                <View style={styles.sessionHeroContent}>
                  <Text style={[styles.sessionHeroTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession.title}
                  </Text>
                  <Text style={[styles.sessionHeroSubtitle, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedSession.subtitle}
                  </Text>
                  
                  <View style={styles.sessionHeroInfo}>
                    <View style={styles.heroInfoItem}>
                      <MaterialCommunityIcons name="calendar" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                      <Text style={[styles.heroInfoText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {formatDate(selectedSession.startTime)}
                      </Text>
                    </View>
                    
                    <View style={styles.heroInfoItem}>
                      <MaterialCommunityIcons name="clock" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                      <Text style={[styles.heroInfoText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {formatTime(selectedSession.startTime)} - {formatTime(selectedSession.endTime)}
                      </Text>
                    </View>
                    
                    <View style={styles.heroInfoItem}>
                      <MaterialCommunityIcons name="clock-outline" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                      <Text style={[styles.heroInfoText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {selectedSession.duration}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
            
            {/* Quick Actions */}
            <View style={styles.quickActions}>
              {selectedSession.status === 'live' ? (
                <TouchableOpacity
                  style={[styles.quickActionButton, styles.joinAction]}
                  onPress={() => {
                    setShowSessionDetails(false);
                    setShowJoinModal(true);
                  }}
                >
                  <MaterialCommunityIcons name="video" size={24} color="#ffffff" />
                  <Text style={styles.joinActionText}>Join Live Session</Text>
                </TouchableOpacity>
              ) : selectedSession.status === 'upcoming' ? (
                <>
                  <TouchableOpacity
                    style={[styles.quickActionButton, styles.reminderAction]}
                    onPress={() => scheduleReminder(selectedSession)}
                  >
                    <MaterialCommunityIcons 
                      name={selectedSession.reminderSet ? "bell" : "bell-outline"} 
                      size={24} 
                      color="#ffffff" 
                    />
                    <Text style={styles.reminderActionText}>
                      {selectedSession.reminderSet ? 'Reminder Set' : 'Set Reminder'}
                    </Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.quickActionButton, styles.calendarAction]}
                    onPress={() => addToCalendar(selectedSession)}
                  >
                    <MaterialCommunityIcons name="calendar-plus" size={24} color="#ffffff" />
                    <Text style={styles.calendarActionText}>Add to Calendar</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <TouchableOpacity
                  style={[styles.quickActionButton, styles.recordingAction]}
                  onPress={() => {
                    if (selectedSession.recordingLink) {
                      Linking.openURL(selectedSession.recordingLink);
                    } else {
                      Alert.alert('Recording', 'Recording will be available soon.');
                    }
                  }}
                >
                  <MaterialCommunityIcons name="play-circle" size={24} color="#ffffff" />
                  <Text style={styles.recordingActionText}>Watch Recording</Text>
                </TouchableOpacity>
              )}
            </View>
            
            {/* Description */}
            <View style={styles.descriptionSection}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Description
              </Text>
              <Text style={[styles.descriptionText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                {selectedSession.longDescription}
              </Text>
            </View>
            
            {/* Instructor */}
            <View style={styles.instructorSection}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Instructor
              </Text>
              <View style={[styles.instructorCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                <Image 
                  source={{ uri: (selectedSession.instructorPhoto && typeof selectedSession.instructorPhoto=="string" && selectedSession.instructorPhoto.startsWith("http")) ? selectedSession.instructorPhoto : undefined }} 
                  style={styles.detailedInstructorImage}
                />
                <View style={styles.detailedInstructorInfo}>
                  <Text style={[styles.detailedInstructorName, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession.instructor}
                  </Text>
                  <Text style={[styles.detailedInstructorBio, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    {selectedSession.instructorBio}
                  </Text>
                  <View style={styles.instructorStats}>
                    <View style={styles.instructorStat}>
                      <MaterialCommunityIcons name="star" size={16} color="#fbbf24" />
                      <Text style={[styles.instructorStatText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {selectedSession.instructorRating.toFixed(1)} rating
                      </Text>
                    </View>
                    <View style={styles.instructorStat}>
                      <MaterialCommunityIcons name="video" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                      <Text style={[styles.instructorStatText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {selectedSession.sessionsConducted} sessions
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
            
            {/* Session Details */}
            <View style={styles.detailsSection}>
              <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                Session Details
              </Text>
              
              <View style={styles.detailsGrid}>
                <View style={[styles.detailCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="account-group" size={24} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailCardValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession.currentParticipants}/{selectedSession.maxParticipants}
                  </Text>
                  <Text style={[styles.detailCardLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Participants
                  </Text>
                </View>
                
                <View style={[styles.detailCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="video" size={24} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <Text style={[styles.detailCardValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession.platform}
                  </Text>
                  <Text style={[styles.detailCardLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Platform
                  </Text>
                </View>
                
                <View style={[styles.detailCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="certificate" size={24} color={selectedSession.certificateAvailable ? "#10b981" : (darkMode ? '#a0aec0' : '#6b7280')} />
                  <Text style={[
                    styles.detailCardValue, 
                    { color: selectedSession.certificateAvailable ? "#10b981" : (darkMode ? '#f7fafc' : '#1f2937') }
                  ]}>
                    {selectedSession.certificateAvailable ? 'Yes' : 'No'}
                  </Text>
                  <Text style={[styles.detailCardLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Certificate
                  </Text>
                </View>
                
                <View style={[styles.detailCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
                  <MaterialCommunityIcons name="star" size={24} color="#fbbf24" />
                  <Text style={[styles.detailCardValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession.rating.toFixed(1)}
                  </Text>
                  <Text style={[styles.detailCardLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Rating
                  </Text>
                </View>
              </View>
            </View>
            
            {/* Features */}
            {selectedSession.status !== 'past' && (
              <View style={styles.featuresSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  Session Features
                </Text>
                <View style={styles.featuresList}>
                  {selectedSession.qnaEnabled && (
                    <View style={styles.featureItem}>
                      <MaterialCommunityIcons name="comment-question" size={20} color="#667eea" />
                      <Text style={[styles.featureText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Live Q&A
                      </Text>
                    </View>
                  )}
                  {selectedSession.pollEnabled && (
                    <View style={styles.featureItem}>
                      <MaterialCommunityIcons name="poll" size={20} color="#10b981" />
                      <Text style={[styles.featureText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Interactive Polls
                      </Text>
                    </View>
                  )}
                  {selectedSession.whiteboardEnabled && (
                    <View style={styles.featureItem}>
                      <MaterialCommunityIcons name="draw" size={20} color="#f59e0b" />
                      <Text style={[styles.featureText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Digital Whiteboard
                      </Text>
                    </View>
                  )}
                  {selectedSession.breakoutRooms && (
                    <View style={styles.featureItem}>
                      <MaterialCommunityIcons name="account-group" size={20} color="#ef4444" />
                      <Text style={[styles.featureText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Breakout Rooms
                      </Text>
                    </View>
                  )}
                  {selectedSession.chatEnabled && (
                    <View style={styles.featureItem}>
                      <MaterialCommunityIcons name="message-text" size={20} color="#9B30FF" />
                      <Text style={[styles.featureText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        Group Chat
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            )}
            
            {/* Requirements */}
            {selectedSession.requirements && (
              <View style={styles.requirementsSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  Requirements
                </Text>
                {selectedSession.requirements.map((req, index) => (
                  <View key={index} style={styles.requirementItem}>
                    <MaterialCommunityIcons name="check" size={16} color={darkMode ? '#a0aec0' : '#6b7280'} />
                    <Text style={[styles.requirementText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {req}
                    </Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Learning Outcomes */}
            {selectedSession.learningOutcomes && (
              <View style={styles.outcomesSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  What You'll Learn
                </Text>
                {selectedSession.learningOutcomes.map((outcome, index) => (
                  <View key={index} style={styles.outcomeItem}>
                    <MaterialCommunityIcons name="check-circle" size={20} color="#10b981" />
                    <Text style={[styles.outcomeText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                      {outcome}
                    </Text>
                  </View>
                ))}
              </View>
            )}
            
            {/* Tags */}
            {selectedSession.tags && (
              <View style={styles.tagsSection}>
                <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  Tags
                </Text>
                <View style={styles.tagsList}>
                  {selectedSession.tags.map((tag, index) => (
                    <View 
                      key={index} 
                      style={[styles.tagItem, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
                    >
                      <Text style={[styles.tagItemText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                        {tag}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </ScrollView>
          
          <View style={[styles.modalActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <View style={styles.priceSection}>
              {selectedSession.isFree ? (
                <Text style={[styles.finalFreePrice, { color: '#10b981' }]}>FREE</Text>
              ) : (
                <>
                  <Text style={[styles.finalPrice, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    ${selectedSession.price}
                  </Text>
                  {selectedSession.originalPrice > selectedSession.price && (
                    <Text style={[styles.finalOriginalPrice, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      ${selectedSession.originalPrice}
                    </Text>
                  )}
                </>
              )}
            </View>
            
            {selectedSession.status === 'past' ? (
              <TouchableOpacity
                style={[styles.modalActionButton, styles.watchRecordingButton]}
                onPress={() => {
                  setShowSessionDetails(false);
                  if (selectedSession.recordingLink) {
                    Linking.openURL(selectedSession.recordingLink);
                  } else {
                    Alert.alert('Recording', 'Recording will be available soon.');
                  }
                }}
              >
                <MaterialCommunityIcons name="play-circle" size={20} color="#ffffff" />
                <Text style={styles.watchRecordingButtonText}>Watch Recording</Text>
              </TouchableOpacity>
            ) : selectedSession.status === 'live' ? (
              <TouchableOpacity
                style={[styles.modalActionButton, styles.joinNowButton]}
                onPress={() => {
                  setShowSessionDetails(false);
                  setShowJoinModal(true);
                }}
              >
                <MaterialCommunityIcons name="video" size={20} color="#ffffff" />
                <Text style={styles.joinNowButtonText}>Join Live Now</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  styles.modalActionButton, 
                  selectedSession.joined ? styles.viewSessionButton : styles.joinSessionButton
                ]}
                onPress={() => {
                  setShowSessionDetails(false);
                  if (!selectedSession.joined) {
                    joinSession(selectedSession);
                  } else {
                    setShowJoinModal(true);
                  }
                }}
              >
                <MaterialCommunityIcons 
                  name={selectedSession.joined ? "eye" : "calendar-plus"} 
                  size={20} 
                  color="#ffffff" 
                />
                <Text style={styles.joinSessionButtonText}>
                  {selectedSession.joined ? 'View Meeting' : 'Join Session'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </SafeAreaView>
      )}
    </Modal>
  );

  const renderJoinModal = () => (
    <Modal
      visible={showJoinModal}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setShowJoinModal(false)}
    >
      <TouchableOpacity 
        style={styles.joinOverlay}
        activeOpacity={1}
        onPress={() => setShowJoinModal(false)}
      >
        <View style={[styles.joinModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.joinHeader}>
            <Text style={[styles.joinTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
              {selectedSession?.status === 'live' ? '🎥 Join Live Session' : '📅 Session Details'}
            </Text>
            <TouchableOpacity onPress={() => setShowJoinModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color={darkMode ? '#ffffff' : '#1f2937'} />
            </TouchableOpacity>
          </View>
          
          <View style={styles.joinContent}>
            <LinearGradient
              colors={[selectedSession?.color || '#667eea', `${selectedSession?.color || '#667eea'}80`]}
              style={styles.joinIcon}
            >
              <MaterialCommunityIcons 
                name={selectedSession?.status === 'live' ? "video" : "calendar"} 
                size={40} 
                color="#ffffff" 
              />
            </LinearGradient>
            
            <Text style={[styles.joinSessionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
              {selectedSession?.title}
            </Text>
            
            <View style={styles.joinDetails}>
              <View style={styles.joinDetailItem}>
                <MaterialCommunityIcons name="calendar" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <View style={styles.joinDetailInfo}>
                  <Text style={[styles.joinDetailLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Date & Time
                  </Text>
                  <Text style={[styles.joinDetailValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession && formatDate(selectedSession.startTime)} at {selectedSession && formatTime(selectedSession.startTime)}
                  </Text>
                </View>
              </View>
              
              <View style={styles.joinDetailItem}>
                <MaterialCommunityIcons name="clock-outline" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <View style={styles.joinDetailInfo}>
                  <Text style={[styles.joinDetailLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Duration
                  </Text>
                  <Text style={[styles.joinDetailValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession?.duration}
                  </Text>
                </View>
              </View>
              
              <View style={styles.joinDetailItem}>
                <MaterialCommunityIcons name="video" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                <View style={styles.joinDetailInfo}>
                  <Text style={[styles.joinDetailLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                    Platform
                  </Text>
                  <Text style={[styles.joinDetailValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                    {selectedSession?.platform}
                  </Text>
                </View>
              </View>
              
              {selectedSession?.meetingId && (
                <View style={styles.joinDetailItem}>
                  <MaterialCommunityIcons name="key" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.joinDetailInfo}>
                    <Text style={[styles.joinDetailLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Meeting ID
                    </Text>
                    <Text style={[styles.joinDetailValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedSession.meetingId}
                    </Text>
                  </View>
                </View>
              )}
              
              {selectedSession?.meetingPassword && (
                <View style={styles.joinDetailItem}>
                  <MaterialCommunityIcons name="lock" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
                  <View style={styles.joinDetailInfo}>
                    <Text style={[styles.joinDetailLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                      Password
                    </Text>
                    <Text style={[styles.joinDetailValue, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                      {selectedSession.meetingPassword}
                    </Text>
                  </View>
                </View>
              )}
            </View>
            
            <Text style={[styles.joinInstructions, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
              {selectedSession?.status === 'live' 
                ? 'Click "Join Now" to open the meeting in your preferred app.'
                : 'Save these details for when the session starts.'}
            </Text>
          </View>
          
          <View style={[styles.joinActions, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <TouchableOpacity
              style={[styles.joinActionButton, styles.copyButton]}
              onPress={async () => {
                if (selectedSession) {
                  await Clipboard.setStringAsync(
                    `Meeting Details:\n\nTitle: ${selectedSession.title}\nDate: ${formatDate(selectedSession.startTime)}\nTime: ${formatTime(selectedSession.startTime)}\nPlatform: ${selectedSession.platform}\nMeeting ID: ${selectedSession.meetingId}\nPassword: ${selectedSession.meetingPassword}\nLink: ${selectedSession.meetingLink}`
                  );
                  Alert.alert('Copied!', 'Meeting details copied to clipboard.');
                }
              }}
            >
              <MaterialCommunityIcons name="content-copy" size={20} color="#667eea" />
              <Text style={styles.copyButtonText}>Copy Details</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[styles.joinActionButton, styles.joinNowButton]}
              onPress={() => {
                if (selectedSession) {
                  openSessionLink(selectedSession);
                  setShowJoinModal(false);
                }
              }}
            >
              <MaterialCommunityIcons name="video" size={20} color="#ffffff" />
              <Text style={styles.joinNowButtonText}>
                {selectedSession?.status === 'live' ? 'Join Now' : 'Save & Close'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const getFilterCount = () => {
    let count = 0;
    if (selectedFilters.category !== 'all') count++;
    if (selectedFilters.price !== 'all') count++;
    return count;
  };

  if (loading) {
    return renderLoading();
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar 
        barStyle="light-content" 
        backgroundColor="#ef4444" 
      />
      
      {renderHeader()}
      {renderSearchBar()}
      
      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#ef4444']}
            tintColor="#ef4444"
          />
        }
        scrollEventThrottle={16}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: false }
        )}
      >
        {renderLiveNowBanner()}
        {renderTabs()}
        
        {/* Filter Summary */}
        {getFilterCount() > 0 && (
          <View style={styles.filterSummary}>
            <Text style={[styles.filterSummaryText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
              Active filters: {getFilterCount()}
            </Text>
            <TouchableOpacity onPress={() => {
              setSelectedFilters({ category: 'all', instructor: 'all', price: 'all' });
            }}>
              <Text style={styles.clearFiltersText}>Clear all</Text>
            </TouchableOpacity>
          </View>
        )}
        
        <View style={styles.sessionsList}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            {activeTab === 'upcoming' ? 'Upcoming Sessions' :
             activeTab === 'live' ? 'Live Now' :
             activeTab === 'past' ? 'Past Sessions' : 'My Sessions'}
            <Text style={styles.sessionCount}> ({filteredSessions.length})</Text>
          </Text>
          
          {filteredSessions.length === 0 ? (
            renderEmptyState()
          ) : (
            filteredSessions.map((session) => renderSessionCard(session))
          )}
        </View>
        
        {/* Categories Section */}
        <View style={styles.categoriesSection}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            Browse by Category
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoriesScroll}>
            {categories.slice(1).map((category) => (
              <TouchableOpacity
                key={category.id}
                style={[
                  styles.categoryCard,
                  { backgroundColor: getCategoryColor(category.id) + '20' },
                ]}
                onPress={() => {
                  setSelectedFilters({...selectedFilters, category: category.id});
                  setActiveTab('upcoming');
                }}
              >
                <LinearGradient
                  colors={[getCategoryColor(category.id), `${getCategoryColor(category.id)}80`]}
                  style={styles.categoryCardIcon}
                >
                  <MaterialCommunityIcons name={category.icon} size={32} color="#ffffff" />
                </LinearGradient>
                <Text style={[styles.categoryCardTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
                  {category.label}
                </Text>
                <Text style={[styles.categoryCardCount, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  {liveSessions.filter(s => s.category === category.id).length} sessions
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
        
        {/* Quick Tips */}
        <View style={styles.tipsSection}>
          <Text style={[styles.sectionTitle, { color: darkMode ? '#f7fafc' : '#1f2937' }]}>
            💡 Tips for Live Sessions
          </Text>
          <View style={[styles.tipsCard, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="wifi" size={20} color="#667eea" />
              <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Ensure stable internet connection
              </Text>
            </View>
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="headphones" size={20} color="#10b981" />
              <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Use headphones for better audio
              </Text>
            </View>
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="microphone" size={20} color="#f59e0b" />
              <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Mute your mic when not speaking
              </Text>
            </View>
            <View style={styles.tipItem}>
              <MaterialCommunityIcons name="help-circle" size={20} color="#ef4444" />
              <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
                Prepare questions in advance
              </Text>
            </View>
          </View>
        </View>
        
        {/* Spacer for bottom padding */}
        <View style={{ height: 100 }} />
      </Animated.ScrollView>
      
      {/* Modals */}
      {renderFiltersModal()}
      {renderSessionDetailsModal()}
      {renderJoinModal()}
      
      {/* Floating Action Button */}
      <TouchableOpacity
        style={[styles.fab, { backgroundColor: '#ef4444' }]}
        onPress={() => navigation.navigate('ScheduleSession')}
      >
        <MaterialCommunityIcons name="plus" size={24} color="#ffffff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const darkMode = (() => {
  try {
    // Try to get from cache or use default
    if (typeof getDarkModeSync === 'function') {
      return getDarkModeSync();
    }
    return false;
  } catch (error) {
    return false;
  }
})();

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#ef4444',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    overflow: 'hidden',
  },
  headerGradient: {
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
    paddingBottom: 20,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  menuButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  iconButton: {
    padding: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 16,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 12,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  searchContainer: {
    position: 'absolute',
    top: 180,
    left: 20,
    right: 20,
    zIndex: 99,
    borderRadius: 16,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    marginHorizontal: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  filterBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  scrollView: {
    flex: 1,
    marginTop: 200,
  },
  contentContainer: {
    paddingBottom: 100,
  },
  liveBanner: {
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 16,
    overflow: 'hidden',
  },
  liveBannerGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  liveIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  livePulse: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#ffffff',
    marginRight: 8,
  },
  liveText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  liveBannerText: {
    flex: 1,
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginHorizontal: 12,
  },
  tabsContainer: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  tabButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  tabCount: {
    marginLeft: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
  },
  tabCountText: {
    fontSize: 12,
    fontWeight: '600',
  },
  filterSummary: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    marginBottom: 16,
  },
  filterSummaryText: {
    fontSize: 14,
  },
  clearFiltersText: {
    fontSize: 14,
    color: '#ef4444',
    fontWeight: '600',
  },
  sessionsList: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  sessionCount: {
    fontSize: 14,
    fontWeight: 'normal',
    opacity: 0.7,
  },
  sessionCard: {
    marginBottom: 20,
  },
  sessionCardContent: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  statusBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    zIndex: 2,
    gap: 4,
  },
  statusText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  sessionImageContainer: {
    height: 200,
    position: 'relative',
  },
  sessionImage: {
    width: '100%',
    height: '100%',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 80,
  },
  sessionImageInfo: {
    position: 'absolute',
    bottom: 12,
    left: 12,
    right: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  categoryText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  sessionTimeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  sessionDateText: {
    color: '#ffffff',
    fontSize: 10,
  },
  sessionTimeText: {
    color: '#ffffff',
    fontSize: 10,
  },
  sessionContent: {
    padding: 16,
  },
  sessionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sessionTitleContainer: {
    flex: 1,
  },
  sessionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  sessionSubtitle: {
    fontSize: 12,
  },
  joinedBadge: {
    padding: 4,
  },
  instructorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  instructorImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 8,
  },
  instructorDetails: {
    flex: 1,
  },
  instructorName: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  instructorRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  ratingText: {
    fontSize: 12,
  },
  sessionsText: {
    fontSize: 12,
  },
  sessionDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  detailText: {
    fontSize: 12,
  },
  countdownContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    gap: 6,
  },
  countdownText: {
    fontSize: 12,
    fontWeight: '600',
  },
  sessionTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  tagText: {
    fontSize: 10,
  },
  sessionActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  freePrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  currentPrice: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  originalPrice: {
    fontSize: 14,
    textDecorationLine: 'line-through',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  joinButton: {
    backgroundColor: '#ef4444',
  },
  viewButton: {
    backgroundColor: '#667eea',
  },
  recordingButton: {
    backgroundColor: '#10b981',
  },
  joinButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  recordingButtonText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
    paddingHorizontal: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ef4444',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  emptyStateButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  categoriesSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  categoriesScroll: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  categoryCard: {
    width: 140,
    padding: 16,
    borderRadius: 16,
    marginRight: 12,
    alignItems: 'center',
  },
  categoryCardIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  categoryCardTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  categoryCardCount: {
    fontSize: 12,
    textAlign: 'center',
  },
  tipsSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  tipsCard: {
    padding: 16,
    borderRadius: 16,
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },
  tipText: {
    flex: 1,
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  joinOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  filtersModal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
  },
  filtersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: darkMode ? '#4a5568' : '#e5e7eb',
  },
  filtersTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  filtersContent: {
    padding: 20,
  },
  filterSection: {
    marginBottom: 24,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  filterOptionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  filtersActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  filterActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    flex: 1,
    gap: 8,
  },
  resetButton: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderWidth: 1,
    borderColor: '#ef4444',
  },
  resetButtonText: {
    color: '#ef4444',
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    backgroundColor: '#ef4444',
  },
  applyButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: Platform.OS === 'ios' ? 40 : 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 16,
  },
  modalContent: {
    flex: 1,
  },
  sessionHero: {
    height: 300,
    position: 'relative',
  },
  sessionHeroImage: {
    width: '100%',
    height: '100%',
  },
  sessionHeroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heroStatusBadge: {
    position: 'absolute',
    top: 20,
    left: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    zIndex: 2,
    gap: 6,
  },
  heroStatusText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  sessionHeroGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 120,
  },
  sessionHeroContent: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    right: 20,
  },
  sessionHeroTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  sessionHeroSubtitle: {
    fontSize: 16,
    marginBottom: 16,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  sessionHeroInfo: {
    gap: 8,
  },
  heroInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  heroInfoText: {
    fontSize: 14,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  quickActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  quickActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  joinAction: {
    backgroundColor: '#ef4444',
  },
  reminderAction: {
    backgroundColor: '#f59e0b',
  },
  calendarAction: {
    backgroundColor: '#667eea',
  },
  recordingAction: {
    backgroundColor: '#10b981',
  },
  joinActionText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  reminderActionText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  calendarActionText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  recordingActionText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  descriptionSection: {
    padding: 20,
    paddingTop: 0,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  descriptionText: {
    fontSize: 16,
    lineHeight: 24,
  },
  instructorSection: {
    padding: 20,
    paddingTop: 0,
  },
  instructorCard: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  detailedInstructorImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    marginRight: 16,
  },
  detailedInstructorInfo: {
    flex: 1,
  },
  detailedInstructorName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  detailedInstructorBio: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  instructorStats: {
    flexDirection: 'row',
    gap: 16,
  },
  instructorStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  instructorStatText: {
    fontSize: 12,
  },
  detailsSection: {
    padding: 20,
    paddingTop: 0,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  detailCard: {
    width: '48%',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  detailCardValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginVertical: 8,
  },
  detailCardLabel: {
    fontSize: 12,
  },
  featuresSection: {
    padding: 20,
    paddingTop: 0,
  },
  featuresList: {
    gap: 12,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  featureText: {
    fontSize: 14,
  },
  requirementsSection: {
    padding: 20,
    paddingTop: 0,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  requirementText: {
    flex: 1,
    fontSize: 14,
  },
  outcomesSection: {
    padding: 20,
    paddingTop: 0,
  },
  outcomeItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    gap: 8,
  },
  outcomeText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  tagsSection: {
    padding: 20,
    paddingTop: 0,
  },
  tagsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagItem: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  tagItemText: {
    fontSize: 12,
  },
  modalActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 16,
    alignItems: 'center',
  },
  priceSection: {
    flex: 1,
  },
  finalFreePrice: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  finalPrice: {
    fontSize: 28,
    fontWeight: 'bold',
  },
  finalOriginalPrice: {
    fontSize: 16,
    textDecorationLine: 'line-through',
  },
  modalActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    flex: 2,
    gap: 8,
  },
  watchRecordingButton: {
    backgroundColor: '#10b981',
  },
  joinNowButton: {
    backgroundColor: '#ef4444',
  },
  joinSessionButton: {
    backgroundColor: '#ef4444',
  },
  viewSessionButton: {
    backgroundColor: '#667eea',
  },
  watchRecordingButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  joinNowButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  joinSessionButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  joinModal: {
    width: width * 0.9,
    borderRadius: 20,
    overflow: 'hidden',
  },
  joinHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: darkMode ? '#2d3748' : '#f3f4f6',
  },
  joinTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  joinContent: {
    padding: 20,
    alignItems: 'center',
  },
  joinIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  joinSessionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
  },
  joinDetails: {
    width: '100%',
    gap: 12,
    marginBottom: 20,
  },
  joinDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  joinDetailInfo: {
    flex: 1,
  },
  joinDetailLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  joinDetailValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  joinInstructions: {
    fontSize: 14,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  joinActions: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  joinActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    flex: 1,
    gap: 8,
  },
  copyButton: {
    backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
    borderWidth: 1,
    borderColor: '#667eea',
  },
  copyButtonText: {
    color: '#667eea',
    fontSize: 16,
    fontWeight: '600',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});


export default LiveSessions;
